import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ReferralLinkDropdown } from '@pages/IntroducingBroker/LinkHub/Links/components/ReferralLinkDropdown'
import { ReferralLinkUrlFields } from '@pages/IntroducingBroker/LinkHub/Links/components/ReferralLinkUrlFields'
import { SectionHeader } from '@pages/IntroducingBroker/LinkHub/Links/components/SectionHeader'
import { getLandingPageUrl, getLinkStyles } from '@pages/IntroducingBroker/LinkHub/Links/helpers'
import { useLinkHub } from '@pages/IntroducingBroker/LinkHub/Links/hooks/useLinkHub'
import { LinkProps } from '@pages/IntroducingBroker/LinkHub/Links/types'

import { Button } from '@ui/components/Button'

import { useWindowResize } from '@utils/domUtils'

import { IntroducingBrokerReferralTypes } from '@model/IntroducingBrokerDetailsDto'

import { Box } from '@shared/components/Box'

export const PredefinedLink = ({
  ibCode,
  mtCode,
  ibLink,
  mtLink,
  fetchShortenedIBLink,
}: LinkProps) => {
  const { links, shortening, landingPages } = useLinkHub({
    ibCode,
    mtCode,
    ibLink,
    mtLink,
    fetchShortenedIBLink,
    isCustomType: true,
  })

  const { mainReferralLink, mtReferralLink, resetReferralLinks } = links
  const {
    shortenedMainReferralLink,
    setShortenedMainReferralLink,
    shortenedMTReferralLink,
    isShortMainLinkLoading,
    isShortMTLinkLoading,
    isShorteningAvailable,
    handleIBLinkShortening,
  } = shortening
  const {
    landingPageUrl,
    setLandingPageUrl,
    customLandingPageUrl,
    isCustomLpUrlSelected,
    previewLandingPages,
    isLoadingPreviewLpUrlsFromCategories,
  } = landingPages

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const [ibCodeId, setIbCodeId] = useState<IntroducingBrokerReferralTypes>(
    IntroducingBrokerReferralTypes.Default
  )

  const resolvedLandingPageUrl = getLandingPageUrl({
    isCustomLpUrlSelected,
    customLandingPageUrl,
    id: landingPageUrl?.id,
  })

  const linkStyles = getLinkStyles(isMobile)
  const hasMultipleCodes = !!ibCode && !!mtCode
  const hasSingleCode = !!ibCode || !!mtCode

  const activeReferralType = hasMultipleCodes
    ? ibCodeId
    : ibCode
    ? IntroducingBrokerReferralTypes.Default
    : IntroducingBrokerReferralTypes.MultiTier

  return (
    <Box flex direction='column' fullWidth>
      <SectionHeader
        title={t('IB.LinkHubPage.Predefined referral link')}
        activeTab={ibCodeId}
        setActiveTab={setIbCodeId}
        shouldRenderSegmentedControl={hasMultipleCodes}
      />
      <Box mt={hasSingleCode ? linkStyles.mt : 'x8'}>
        <Box flex direction='column' gap='x8'>
          {hasSingleCode && (
            <ReferralLinkDropdown
              label={t('Referral Materials.Default target URLs')}
              landingPageUrl={landingPageUrl}
              setLandingPageUrl={setLandingPageUrl}
              shortenedReferralLink={shortenedMainReferralLink}
              setShortenedReferralLink={setShortenedMainReferralLink}
              landingPageUrlsFromCategories={previewLandingPages}
              isLoadingLpUrlsFromCategories={isLoadingPreviewLpUrlsFromCategories}
              resetReferralLinks={resetReferralLinks}
            />
          )}
          <Button.Secondary
            size='md'
            fullWidth
            disabled={!landingPageUrl}
            onClick={() => landingPageUrl && window.open(landingPageUrl.name, '_blank')}
          >
            {t('Preview')}
          </Button.Secondary>
        </Box>
      </Box>
      <ReferralLinkUrlFields
        activeReferralType={activeReferralType}
        mainReferralLink={mainReferralLink}
        mtReferralLink={mtReferralLink}
        shortenedMainReferralLink={shortenedMainReferralLink}
        shortenedMTReferralLink={shortenedMTReferralLink}
        isShortMainLinkLoading={isShortMainLinkLoading}
        isShortMTLinkLoading={isShortMTLinkLoading}
        isShorteningAvailable={isShorteningAvailable}
        handleIBLinkShortening={handleIBLinkShortening}
        isCustomLpUrlSelected={isCustomLpUrlSelected}
        landingPageUrl={landingPageUrl}
        resolvedLandingPageUrl={resolvedLandingPageUrl}
        ibCode={ibCode}
        mtCode={mtCode}
        mtLink={mtLink}
      />
    </Box>
  )
}
